import { FC, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { message } from 'antd';
import { useCountdown } from 'usehooks-ts';
import { ConfirmModal } from 'components/Shared/ConfirmModal';
import { Spin } from 'components/UI/Spin';
import * as Styled from './styles';
import { resendVerificationEmail } from 'appRedux/restApi';
import { getPath } from 'utils';
import { ReactQueryKeys } from 'constants/app/react-query-keys';
import BellImage from 'assets/images/bell.png';
import { RestAxiosError } from 'interfaces';

type CheckEmailModalProps = {
  checkEmailModalVisible: boolean;
  email: string;
  resendOnMount?: boolean;
};

const COUNT_START = 30;
const INTERVAL = 1000;

export const CheckEmailModal: FC<CheckEmailModalProps> = ({
  checkEmailModalVisible,
  email,
  resendOnMount = false
}) => {
  const { t } = useTranslation();
  const [count, { startCountdown, resetCountdown }] = useCountdown({
    countStart: COUNT_START,
    intervalMs: INTERVAL
  });
  const [goToHomeButtonLoading, setGoToHomeButtonLoading] = useState(false);
  const {
    refetch,
    isRefetching: resendButtonLoading,
    data,
    error
  } = useQuery<
    Awaited<ReturnType<typeof resendVerificationEmail>>,
    RestAxiosError
  >({
    queryKey: [ReactQueryKeys.RESEND_EMAIL, email],
    queryFn: () => resendVerificationEmail(email),
    enabled: false,
    retry: false
  });

  const transitionToHome = useCallback(() => {
    setGoToHomeButtonLoading(true);
    window.location.href = getPath('/');
  }, []);

  const resendEmail = useCallback(() => {
    refetch();
  }, [email]);

  useEffect(() => {
    resetCountdown();
    startCountdown();
  }, [data]);

  useEffect(() => {
    if (checkEmailModalVisible && resendOnMount) {
      resendEmail();
    }
  }, [resendOnMount, checkEmailModalVisible]);

  useEffect(() => {
    if (error) {
      message.error(error.response?.data.message);
    }
  }, [error]);

  return (
    <ConfirmModal
      title={t('account.checkEmailModalTitle')}
      description={t('account.checkEmailModalDescription', { email: email })}
      confirmText={t('account.checkEmailModalConfirmText')}
      cancelText={t('account.checkEmailModalResendText')}
      image={BellImage}
      imageSizes={[80, 80]}
      coloredTitle
      onCancel={resendEmail}
      onClose={() => {}}
      cancelWithButton
      visible={checkEmailModalVisible}
      onSubmit={transitionToHome}
      loading={{
        submit: goToHomeButtonLoading,
        cancel: resendButtonLoading
      }}
      disabled={{
        cancel: !!count
      }}
      closable={false}
    >
      {!!count && (
        <Styled.ResendHint>
          <Spin type="fit" />
          <Styled.Counter
            dangerouslySetInnerHTML={{
              __html: t('account.checkEmailModalResendCountdown', { count })
            }}
          />
        </Styled.ResendHint>
      )}
    </ConfirmModal>
  );
};
